(function () {
  if (customElements.get('ajax-collection-showcase')) return;

  const responseCache = (window.DearmoraAjaxCollectionCache =
    window.DearmoraAjaxCollectionCache || new Map());

  class AjaxCollectionShowcase extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.configElement = this.querySelector('[data-ajax-collection-config]');
      this.grid = this.querySelector('[data-ajax-collection-grid]');
      this.status = this.querySelector('[data-ajax-collection-status]');
      this.retryButton = this.querySelector('[data-ajax-collection-retry]');
      this.observer = null;
      this.requestPromise = null;

      if (!this.configElement || !this.grid || !this.status) return;

      try {
        this.config = JSON.parse(this.configElement.textContent);
      } catch (error) {
        return;
      }

      this.dataset.initialized = 'true';
      this.addEventListener('kids:load-collection', () => this.load());
      this.retryButton?.addEventListener('click', () => {
        responseCache.delete(this.cacheKey());
        this.load(true);
      });

      this.observe();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.observer = null;
          this.load();
        },
        { rootMargin: '500px 0px' }
      );

      this.observer.observe(this);
    }

    cacheKey() {
      return `${this.config.handle || ''}:ajax-collection-showcase-products`;
    }

    async load(forceReload = false) {
      if (this.dataset.loaded === 'true' && !forceReload) return;
      if (this.requestPromise && !forceReload) return this.requestPromise;

      this.observer?.disconnect();
      this.observer = null;

      if (!this.config.handle) {
        this.renderMessage(this.config.emptyText, 'empty');
        return;
      }

      this.renderLoading();
      this.requestPromise = this.fetchProducts(forceReload)
        .then((responseData) => this.renderProducts(responseData))
        .catch(() => this.renderError())
        .finally(() => {
          this.requestPromise = null;
        });

      return this.requestPromise;
    }

    fetchProducts(forceReload) {
      const key = this.cacheKey();
      if (!forceReload && responseCache.has(key)) return responseCache.get(key);

      const rootUrl = this.normalizeRootUrl(this.config.rootUrl);
      const requestUrl = `${rootUrl}collections/${encodeURIComponent(
        this.config.handle
      )}?section_id=ajax-collection-showcase-products`;

      const request = fetch(requestUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const responseDocument = new DOMParser().parseFromString(html, 'text/html');
          const responseRoot = responseDocument.querySelector(
            '[data-ajax-collection-showcase-response]'
          );
          const responseList = responseRoot?.querySelector(
            '[data-ajax-collection-showcase-list]'
          );

          if (!responseRoot || !responseList) {
            throw new Error('Collection response markup is invalid');
          }

          return {
            collectionUrl: responseRoot.dataset.collectionUrl || '',
            items: Array.from(responseList.children).map((item) => item.outerHTML)
          };
        })
        .catch((error) => {
          responseCache.delete(key);
          throw error;
        });

      responseCache.set(key, request);
      return request;
    }

    renderLoading() {
      this.dataset.state = 'loading';
      this.setAttribute('aria-busy', 'true');
      this.grid.hidden = false;
      this.grid.replaceChildren();
      this.status.hidden = false;
      this.status.dataset.state = 'loading';
      this.status.textContent = this.config.loadingText;
      this.retryButton?.setAttribute('hidden', '');

      const count = Math.min(
        Number(this.config.limit) || 5,
        Number(this.config.desktopColumns) || 5
      );

      for (let index = 0; index < count; index += 1) {
        const skeleton = document.createElement('li');
        skeleton.className = 'ajax-collection-showcase__skeleton';
        skeleton.setAttribute('aria-hidden', 'true');
        skeleton.innerHTML =
          '<span class="ajax-collection-showcase__skeleton-media"></span>' +
          '<span class="ajax-collection-showcase__skeleton-line"></span>' +
          '<span class="ajax-collection-showcase__skeleton-line ajax-collection-showcase__skeleton-line--short"></span>';
        this.grid.appendChild(skeleton);
      }
    }

    renderProducts(responseData) {
      const items = Array.isArray(responseData?.items) ? responseData.items : [];
      if (!items.length) {
        this.renderMessage(this.config.emptyText, 'empty');
        return;
      }

      const fragment = document.createDocumentFragment();
      items.slice(0, Number(this.config.limit) || 5).forEach((itemHtml, index) => {
        const template = document.createElement('template');
        template.innerHTML = itemHtml.trim();
        const item = template.content.firstElementChild;
        if (!item) return;

        this.scopeCardIds(item, index);
        fragment.appendChild(item);
      });

      this.grid.replaceChildren(fragment);
      this.grid.hidden = false;
      this.status.hidden = true;
      this.status.textContent = '';
      delete this.status.dataset.state;
      this.dataset.state = 'loaded';
      this.dataset.loaded = 'true';
      this.setAttribute('aria-busy', 'false');

      this.dispatchEvent(
        new CustomEvent('ajax-collection-showcase:rendered', {
          bubbles: true,
          detail: {
            collectionUrl: responseData.collectionUrl || this.config.collectionUrl,
            itemCount: this.grid.children.length
          }
        })
      );
    }

    scopeCardIds(item, index) {
      const suffix = `-${this.id || 'ajax-collection'}-${index}`;
      const idMap = new Map();

      item.querySelectorAll('[id]').forEach((element) => {
        const oldId = element.id;
        const newId = `${oldId}${suffix}`;
        idMap.set(oldId, newId);
        element.id = newId;
      });

      ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for'].forEach((attribute) => {
        item.querySelectorAll(`[${attribute}]`).forEach((element) => {
          const value = element.getAttribute(attribute);
          if (!value) return;
          element.setAttribute(
            attribute,
            value
              .split(/\s+/)
              .map((token) => idMap.get(token) || token)
              .join(' ')
          );
        });
      });
    }

    renderMessage(message, state) {
      this.dataset.state = state;
      this.setAttribute('aria-busy', 'false');
      this.grid.replaceChildren();
      this.grid.hidden = true;
      this.status.hidden = false;
      this.status.dataset.state = state;
      this.status.textContent = message;
      this.retryButton?.setAttribute('hidden', '');
    }

    renderError() {
      this.renderMessage(this.config.errorText, 'error');
      if (!this.retryButton) return;
      this.retryButton.hidden = false;
      this.retryButton.textContent = this.config.retryText;
    }

    normalizeRootUrl(rootUrl) {
      const value = rootUrl || '/';
      return value.endsWith('/') ? value : `${value}/`;
    }
  }

  customElements.define('ajax-collection-showcase', AjaxCollectionShowcase);
})();
